import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SearchParams, LogLevel } from '../lib/api.js';

/**
 * URL parameter names for log search
 */
const URL_PARAMS = {
  from: 'from',
  to: 'to',
  levels: 'levels',
  subApps: 'sub_apps',
  instanceIds: 'instance_ids',
  sessionIds: 'session_ids',
  correlationIds: 'correlation_ids',
  match: 'match',
  regex: 'regex',
  appId: 'app',
  page: 'page',
  pageSize: 'pageSize',
} as const;

/**
 * Parse URL params into SearchParams object
 */
function parseUrlParams(urlParams: URLSearchParams): {
  searchParams: Partial<SearchParams> | null;
  appId: string | null;
  page: number;
  pageSize: number;
} {
  const from = urlParams.get(URL_PARAMS.from);
  const to = urlParams.get(URL_PARAMS.to);
  const levels = urlParams.get(URL_PARAMS.levels);
  const subApps = urlParams.get(URL_PARAMS.subApps);
  const instanceIds = urlParams.get(URL_PARAMS.instanceIds);
  const sessionIds = urlParams.get(URL_PARAMS.sessionIds);
  const correlationIds = urlParams.get(URL_PARAMS.correlationIds);
  const match = urlParams.get(URL_PARAMS.match);
  const regex = urlParams.get(URL_PARAMS.regex);
  const appId = urlParams.get(URL_PARAMS.appId);
  const page = urlParams.get(URL_PARAMS.page);
  const pageSize = urlParams.get(URL_PARAMS.pageSize);

  // If no time range in URL, use defaults
  const hasAnyParam = from || to || levels || subApps || instanceIds ||
    sessionIds || correlationIds || match || regex;

  if (!hasAnyParam) {
    return {
      searchParams: null,
      appId,
      page: page ? parseInt(page, 10) : 1,
      pageSize: pageSize ? parseInt(pageSize, 10) : 100,
    };
  }

  // Build search params - only include from/to if actually present in URL
  // Don't compute defaults here - let the API layer handle defaults
  const searchParams: Partial<SearchParams> = {};

  if (from) {
    searchParams.from = from;
  }
  if (to) {
    searchParams.to = to;
  }

  if (levels) {
    searchParams.levels = levels.split(',') as LogLevel[];
  }

  if (subApps) {
    searchParams.sub_apps = subApps.split(',');
  }

  if (instanceIds) {
    searchParams.instance_ids = instanceIds.split(',');
  }

  if (sessionIds) {
    searchParams.session_ids = sessionIds.split(',');
  }

  if (correlationIds) {
    searchParams.correlation_ids = correlationIds.split(',');
  }

  if (match) {
    searchParams.match = match;
  }

  if (regex) {
    searchParams.regex = regex;
  }

  return {
    searchParams,
    appId,
    page: page ? parseInt(page, 10) : 1,
    pageSize: pageSize ? parseInt(pageSize, 10) : 100,
  };
}

/**
 * Convert SearchParams to URL params
 */
function searchParamsToUrl(
  params: SearchParams | null,
  appId: string | null,
  page: number,
  pageSize: number
): URLSearchParams {
  const urlParams = new URLSearchParams();

  if (appId) {
    urlParams.set(URL_PARAMS.appId, appId);
  }

  if (!params) {
    return urlParams;
  }

  // Only write from/to to URL if they have actual values (not empty strings)
  if (params.from && params.from.trim()) {
    urlParams.set(URL_PARAMS.from, params.from);
  }

  if (params.to && params.to.trim()) {
    urlParams.set(URL_PARAMS.to, params.to);
  }

  if (params.levels && params.levels.length > 0) {
    urlParams.set(URL_PARAMS.levels, params.levels.join(','));
  }

  if (params.sub_apps && params.sub_apps.length > 0) {
    urlParams.set(URL_PARAMS.subApps, params.sub_apps.join(','));
  }

  if (params.instance_ids && params.instance_ids.length > 0) {
    urlParams.set(URL_PARAMS.instanceIds, params.instance_ids.join(','));
  }

  if (params.session_ids && params.session_ids.length > 0) {
    urlParams.set(URL_PARAMS.sessionIds, params.session_ids.join(','));
  }

  if (params.correlation_ids && params.correlation_ids.length > 0) {
    urlParams.set(URL_PARAMS.correlationIds, params.correlation_ids.join(','));
  }

  if (params.match) {
    urlParams.set(URL_PARAMS.match, params.match);
  }

  if (params.regex) {
    urlParams.set(URL_PARAMS.regex, params.regex);
  }

  if (page > 1) {
    urlParams.set(URL_PARAMS.page, String(page));
  }

  if (pageSize !== 100) {
    urlParams.set(URL_PARAMS.pageSize, String(pageSize));
  }

  return urlParams;
}

export interface UseLogSearchParamsOptions {
  defaultRangeHours?: number;
}

export interface UseLogSearchParamsResult {
  /** Parsed search params from URL (null if no search params in URL) */
  urlSearchParams: Partial<SearchParams> | null;
  /** App ID from URL */
  urlAppId: string | null;
  /** Page number from URL */
  urlPage: number;
  /** Page size from URL */
  urlPageSize: number;
  /** Update URL with new search params */
  setUrlSearchParams: (
    params: SearchParams | null,
    appId: string | null,
    page?: number,
    pageSize?: number
  ) => void;
  /** Clear all URL params */
  clearUrlParams: () => void;
}

/**
 * Hook to sync log search params with URL query parameters.
 * Enables shareable links and browser back/forward navigation.
 */
export function useLogSearchParams(
  options: UseLogSearchParamsOptions = {}
): UseLogSearchParamsResult {
  const { defaultRangeHours = 24 } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse current URL params
  const parsed = useMemo(
    () => parseUrlParams(searchParams),
    [searchParams]
  );

  // Update URL with new search params
  const setUrlSearchParams = useCallback(
    (
      params: SearchParams | null,
      appId: string | null,
      page: number = 1,
      pageSize: number = 100
    ) => {
      const newUrlParams = searchParamsToUrl(params, appId, page, pageSize);
      setSearchParams(newUrlParams, { replace: false });
    },
    [setSearchParams]
  );

  // Clear all URL params
  const clearUrlParams = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  return {
    urlSearchParams: parsed.searchParams,
    urlAppId: parsed.appId,
    urlPage: parsed.page,
    urlPageSize: parsed.pageSize,
    setUrlSearchParams,
    clearUrlParams,
  };
}

export default useLogSearchParams;
